import type { IAgentToolContext, IAgentTool } from '../../../../core/ai/agent/IAgentTool';
import { ComplianceChecker } from '../ComplianceChecker';
import type { DiagramData } from '../../../../core/ai/interfaces/IAnalyzer';

export const complianceQueryTool: IAgentTool = {
  name: 'process_compliance_check',
  description: 'Check if this process is SOC 2 ready. Returns a compliance score (0-100) and specific gaps that need attention. Use when the user asks about compliance, audit readiness, or SOC 2.',
  parameters: {
    type: 'object',
    properties: {
      framework: {
        type: 'string',
        enum: ['soc2'],
        default: 'soc2',
        description: 'Compliance framework to check against',
      },
    },
  },
  supportedDiagramTypes: ['flowchart'],
  tier: 'safe',
  category: 'query',

  async execute(_params: Record<string, unknown>, context: IAgentToolContext) {
    const checker = new ComplianceChecker();
    const diagramData: DiagramData = {
      nodes: context.diagramData.nodes.map((n) => ({
        id: n.id,
        type: n.type,
        data: { ...(n.attrs ?? {}), name: n.name, text: n.text, label: n.attrs?.label },
      })),
      edges: context.diagramData.edges.map((e) => ({
        id: e.id,
        type: e.type ?? 'sequence_flow',
        from: e.from,
        to: e.to,
      })),
      diagramType: 'flowchart',
    };

    const insights = checker.analyze(diagramData);

    if (insights.length === 0) {
      return {
        success: false,
        summary: 'At least two process steps or decisions are needed for this compliance check. Add the missing steps, then run the review again.',
        data: { needsInput: true },
      };
    }

    const overallInsight = insights.find((i) => i.id === 'compliance-overall');
    const gaps = insights.filter((i) => i.id !== 'compliance-overall' && i.id !== 'compliance-pack-not-enabled');
    const packNotEnabled = insights.find((i) => i.id === 'compliance-pack-not-enabled');

    if (packNotEnabled) {
      return {
        success: false,
        summary: packNotEnabled.description,
        data: { needsInput: true },
      };
    }

    const gapList = gaps.map((g) => `- ${g.title}`).join('\n');
    const summary = overallInsight
      ? `${overallInsight.title}\n\n${gaps.length > 0 ? `Gaps:\n${gapList}` : 'All checks pass.'}`
      : 'Compliance check completed.';

    return {
      success: true,
      summary,
      data: {
        score: overallInsight?.title.match(/(\d+)%/)?.[1] ?? null,
        gapCount: gaps.length,
        gaps: gaps.map((g) => ({ id: g.id, title: g.title, priority: g.priority, affectedNodeIds: g.affectedNodeIds })),
      },
    };
  },
};
